import React, { useEffect, useState } from "react";
import { FileText, IndianRupee, Send, Timer } from "lucide-react";
import { AES, enc } from "crypto-js";
import swal from "sweetalert";
import PremiumListPage, { StatusBadge } from "../../../Components/Dashboard/PremiumListPage";
import DocumentDetailLayout from "../../../Components/documents/DocumentDetailLayout";
import { BASE_URL } from "../CommonApi";
import QuotesUpdate from "./QuotesUpdate";

const money = (value) => {
  const amount = Number(String(value ?? 0).replace(/[^0-9.-]/g, ""));
  return `₹ ${(Number.isFinite(amount) ? amount : 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};

function Quotes({ navigation }) {
  const [itemId, setItemID] = useState();
  const [loading, setLoading] = useState(true);
  const [showCard, setShowCard] = useState(true);
  const [showUpdate, setShowUpdate] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [fetchedStatus, setFetchedStatus] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [newinvoiceNumber, setnewInvoice] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [selectedQuote, setSelectedQuote] = useState(null);
  const jwtToken = localStorage.getItem("jwtToken");
  const encryptedUniqueId = localStorage.getItem("uniqueId1");
  const decryptedUniqueId = AES.decrypt(encryptedUniqueId, "1234567812345678").toString(enc.Utf8);

  const fetchInvoices = () => {
    fetch(BASE_URL + `/fetchQuote`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwtToken}`,
      },
      body: JSON.stringify({ uniqueId: decryptedUniqueId }),
    })
      .then((response) => response.json())
      .then((data) => {
        setInvoices(data.status === "success" ? data.responseMessage || [] : []);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching quotes:", error);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchInvoices();
  }, []);

  const handleEdit = (row) => {
    setFetchedStatus(row.status);
    setItemID(row.quoteNumber);
    setShowCard(false);
    setShowDetails(false);
    setShowUpdate(true);
  };

  const handleDelete = (row) => {
    if (!window.confirm("Are you sure? Once deleted, you will not be able to recover this quotation!")) return;

    fetch(BASE_URL + `/deleteByQuoteNumber/${row.quoteNumber}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwtToken}`,
      },
      body: JSON.stringify({ uniqueId: decryptedUniqueId }),
    })
      .then((response) => {
        if (response.status === 200) {
          setInvoices((current) => current.filter((invoice) => invoice.quoteNumber !== row.quoteNumber));
          swal({ icon: "success", title: "Quote Deleted Successfully" });
        }
      })
      .catch((error) => console.error("Error deleting quote:", error));
  };

  const handleInvoice = async (row) => {
    setnewInvoice(row.quoteNumber);
    setCustomerId(row.customerId);
    setSelectedQuote(row);
    setShowDetails(true);
    setShowCard(false);
    setShowUpdate(false);

    try {
      const response = await fetch(BASE_URL + `/fetchByQuoteNumber/${encodeURIComponent(row.quoteNumber)}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwtToken}`,
        },
        body: JSON.stringify({ uniqueId: decryptedUniqueId }),
      });
      const data = await response.json();
      const quoteRows = Array.isArray(data.responseMessage) ? data.responseMessage : [data.responseMessage];
      const quoteDetails = quoteRows[0];

      if (quoteDetails && typeof quoteDetails === "object") {
        setSelectedQuote({ ...row, ...quoteDetails, items: quoteRows });
      }
    } catch (error) {
      console.error("Error fetching quotation details:", error);
    }
  };

  const handleCancelUpdate = () => {
    fetchInvoices();
    setShowUpdate(false);
    setShowCard(true);
  };

  const handleCancelDetails = () => {
    fetchInvoices();
    setShowDetails(false);
    setSelectedQuote(null);
    setShowCard(true);
    setShowUpdate(false);
  };

  if (loading) return <div className="premium-empty-state">Loading quotations...</div>;

  const total = invoices.reduce((sum, item) => sum + Number(item.total || 0), 0);
  const sent = invoices.filter((item) => String(item.status || "").toLowerCase().includes("sent")).length;

  return (
    <div>
      {showCard && (
        <PremiumListPage
          title="Quotations"
          subtitle="Create, track, and manage customer quotations in one place."
          actionLabel="+ New Quote"
          onCreate={() => navigation.navigate("quotesnew")}
          searchPlaceholder="Search quotations..."
          rows={invoices}
          stats={[
            { label: "Total Quotes", value: invoices.length, icon: FileText, tone: "blue" },
            { label: "Total Amount", value: money(total), icon: IndianRupee, tone: "orange" },
            { label: "Sent Quotes", value: sent, icon: Send, tone: "green" },
            { label: "Pending Quotes", value: Math.max(invoices.length - sent, 0), icon: Timer, tone: "red" },
          ]}
          columns={[
            { key: "quoteDate", label: "Date" },
            {
              key: "quoteNumber",
              label: "Quote #",
              render: (row) => (
                <button className="premium-link" onClick={() => handleInvoice(row)}>
                  {row.quoteNumber}
                </button>
              ),
            },
            { key: "orderNumber", label: "Reference" },
            { key: "customerName", label: "Customer Name" },
            {
              key: "status",
              label: "Status",
              render: (row) => <StatusBadge tone="blue">{row.status || "Draft"}</StatusBadge>,
            },
            { key: "total", label: "Amount", className: "premium-money", render: (row) => money(row.total) },
          ]}
          onView={handleInvoice}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      )}

      {showUpdate && <QuotesUpdate invoiceNumber1={itemId} fetchedStatus={fetchedStatus} handleCancel={handleCancelUpdate} />}
      {showDetails && (
        <DocumentDetailLayout
          rows={invoices}
          selectedRow={selectedQuote || invoices.find((quote) => quote.quoteNumber === newinvoiceNumber) || invoices[0]}
          config={{ documentType: "quotation" }}
          onSelect={setSelectedQuote}
          onBack={handleCancelDetails}
          onAction={(action, row) => {
            if (action === "Edit") handleEdit(row);
          }}
        />
      )}
    </div>
  );
}

export default Quotes;
